/**
 * Tasks in **List** — `docs/activity.md` §8.0, §8.1 (Boards 9, 16 C, 19 E).
 *
 * The list engine, unchanged and embedded: chips for the saved views, the
 * toolbar, ticks, *Bulk actions*, the gear with Smart import and Export, the
 * framed table and the phone cards. What is Activity's is only what a screen
 * supplies to any list — the cells, the done circle as the mark, the bulk
 * actions — and the people button's pick, which travels as the one base
 * condition `whose is any of …` (§8.0) and never into the saved view.
 *
 * **Done with Undo** (Board 9 C): the circle ticks a task without opening
 * the row; the row stays struck through until the view is left, and a toast
 * says what it was attached to with **Undo**.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  TASK_VIEW_NAMES, addDays, bucketKeyLabel, dueGroupLabel, priorityKeyLabel, priorityOfKey, zonedLocal,
  type ChecklistProgress, type FilterCondition, type TaskCategory,
} from '@rowted/shared'
import { ListScreen } from '../../components/list-screen.js'
import type { BulkAction } from '../../components/bulk-menu.js'
import { MenuSelect } from '../../components/menu-select.js'
import { DateField } from '../../components/date-field.js'
import { Button } from '../../components/primitives.js'
import { IconCalendar, IconCheck, IconPerson, IconPlus } from '../../frame/icons.js'
import { useList, type ListRow, type UseListResult } from '../../lib/use-list.js'
import { usePhone } from '../../lib/use-narrow.js'
import { cn } from '../../lib/cn.js'
import { activityRequest } from '../api.js'
import { useActivity } from '../activity-context.js'
import { useActivityHostBar } from '../page-views.js'
import { Glyph } from '../glyphs.js'
import { Face, FaceStack, UnassignedFace, type FacePerson } from '../faces.js'
import { openActivityNew } from '../calendar/overlays.js'
import { DoneCircle, LinkChip, LinkChips, rowFaces, usePeopleById } from './parts.js'
import { openTaskSheet, tasksChanged, TASKS_CHANGED_EVENT } from './task-sheet.js'
import { DoneToast } from './done-toast.js'
import type { TaskLink } from './types.js'
import { alsoOnWords, monthDay, rowDue, weekdayShort } from './words.js'
import { ChecklistBadge } from './task-checklist.js'
import { PriorityMark } from './task-priority.js'
import { TASK_CATEGORIES_EVENT, TaskCategoryChips } from './task-categories.js'

/** What every row is drawn from, whatever the view's columns (§8.1). */
const ALWAYS = [
  'done', 'dueAt', 'dueGroup', 'late', 'daysLate', 'onCalendar', 'assigneeName', 'assignees', 'createdAt',
  'links', 'peopleIds', 'faces', 'secondLine', 'bucket', 'taskListName',
  /* Planner's extras (Aaron, Oct 25, notes 1.3 and 1.5; §8.7). */
  'priority', 'categoryChips', 'checklistDone', 'checklistTotal',
]

const BY_BUCKET = 'By bucket'
const MY_OPEN = 'My open'

const str = (value: unknown): string => (value === null || value === undefined ? '' : String(value))
const linksOf = (row: ListRow): TaskLink[] => (Array.isArray(row.links) ? row.links as TaskLink[] : [])
const idsOf = (row: ListRow): string[] => (Array.isArray(row.peopleIds) ? row.peopleIds as string[] : [])
const categoriesOf = (row: ListRow): TaskCategory[] => (Array.isArray(row.categoryChips) ? row.categoryChips as TaskCategory[] : [])
const checklistOf = (row: ListRow): ChecklistProgress | null => {
  const total = Number(row.checklistTotal ?? 0)
  return total > 0 ? { done: Number(row.checklistDone ?? 0), total } : null
}

export function TaskListView({ listId }: { listId: string | null }) {
  const { me, today, people, unassigned, state, change, reloadBadge, directory } = useActivity()
  const phone = usePhone()
  const timeZone = me.user.timeZone
  const baseFilter: FilterCondition[] = useMemo(() => (listId
    ? [{ field: 'taskList', op: 'in', value: [listId] }]
    : [{ field: 'whose', op: 'in', value: [...people, ...unassigned] }]), [listId, people, unassigned])

  const list = useList('tasks', { alwaysFetch: ALWAYS, baseFilter })
  /*
   * On a phone the views are tabs under *Activity* and the count is the line under it, with no chip
   * line between the search box and the list (Aaron, Oct 27) — the page bar's, as a `page` list's are.
   */
  const hostBar = useActivityHostBar(phone)
  const [params] = useSearchParams()
  const byId = usePeopleById()

  /*
   * The views this mode offers: inside a list *By bucket* and *Completed*, which
   * brings back what By bucket leaves out (Aaron, Oct 23); everything but By
   * bucket outside one (Board 9 A, 19 E).
   */
  const views = useMemo(() => list.savedViews.filter((view) => (listId
    ? view.name === BY_BUCKET || view.name === TASK_VIEW_NAMES.completed
    : view.name !== BY_BUCKET)), [list.savedViews, listId])
  const shown: UseListResult = useMemo(() => ({ ...list, savedViews: views }), [list, views])

  /* Open on the remembered view, else *My open* (§8.1) — once per mode, and never over a link that names one. */
  const landed = useRef<string | null>(null)
  useEffect(() => {
    const mode = listId ?? 'people'
    if (landed.current === mode || list.savedViews.length === 0) return
    landed.current = mode
    const active = list.activeViewId ? views.find((view) => view.id === list.activeViewId) : null
    if (active) return
    /* A view somebody handed over in the address (the Board's *+ n more*) is left alone. */
    if (!list.activeViewId && params.get('view')) return
    const remembered = !listId && state.tasks.viewId ? views.find((view) => view.id === state.tasks.viewId) : null
    const fallback = listId ? views.find((view) => view.name === BY_BUCKET) ?? views[0] : views.find((view) => view.name === MY_OPEN)
    const target = remembered ?? fallback
    if (target) list.openSavedView(target.id)
  }, [listId, list, views, state.tasks.viewId, params])

  /* The tab view is remembered for this device (§8.0) — outside a list only. */
  useEffect(() => {
    if (listId || !list.activeViewId || list.activeViewId === state.tasks.viewId) return
    if (!views.some((view) => view.id === list.activeViewId)) return
    change({ tasks: { ...state.tasks, viewId: list.activeViewId } })
  }, [list.activeViewId, listId, views, state.tasks, change])

  /* Ticked here, struck through until the view is left (Board 9 C). */
  const [ticked, setTicked] = useState<Map<string, boolean>>(new Map())
  const viewKey = `${list.activeViewId ?? ''}|${JSON.stringify(list.view.filter ?? null)}|${JSON.stringify(baseFilter)}|${list.search}`
  useEffect(() => { setTicked(new Map()) }, [viewKey])
  const isDone = (row: ListRow): boolean => ticked.get(row.id) ?? row.done === true

  const [toast, setToast] = useState<{ id: string; title: string; also: string | null } | null>(null)

  const tick = useCallback(async (row: ListRow, done: boolean) => {
    setTicked((current) => new Map(current).set(row.id, done))
    try {
      await activityRequest(`/activities/${row.id}/done`, { method: 'POST', body: { done } })
      if (done) setToast({ id: row.id, title: str(row.title), also: alsoOnWords(linksOf(row)) })
      else setToast(null)
      reloadBadge()
      tasksChanged('list')
    } catch {
      setTicked((current) => { const next = new Map(current); next.delete(row.id); return next })
    }
  }, [reloadBadge])

  /* The sheet, the Board or New changed a task: the rows come back from the server. */
  const reload = list.reload
  useEffect(() => {
    const onChanged = (event: Event) => {
      /* Its own circle: the ticked row stays where it is, struck through (Board 9 C). */
      if ((event as CustomEvent<{ source?: string | null } | null>).detail?.source === 'list') return
      reload()
    }
    window.addEventListener(TASKS_CHANGED_EVENT, onChanged)
    /* A category renamed, recoloured or removed: the rows' chips say so (§8.7). */
    window.addEventListener(TASK_CATEGORIES_EVENT, reload)
    return () => {
      window.removeEventListener(TASKS_CHANGED_EVENT, onChanged)
      window.removeEventListener(TASK_CATEGORIES_EVENT, reload)
    }
  }, [reload])

  const facesFor = (row: ListRow): FacePerson[] => {
    const own = rowFaces(row.faces)
    if (own.length > 0) return own.map((face) => byId.get(face.id) ?? face)
    return idsOf(row).map((id) => byId.get(id)).filter((p): p is FacePerson => Boolean(p))
  }

  const dueCell = (row: ListRow) => {
    const due = rowDue(row, timeZone, today, isDone(row))
    if (!due) return <span className="text-[13px] font-medium text-text">—</span>
    const ink = due.tone === 'late' ? 'text-danger font-semibold' : due.tone === 'today' ? 'text-warn font-semibold' : 'text-text font-medium'
    return (
      <span className="flex min-w-0 flex-col">
        <span className={cn('flex items-center gap-[5px] whitespace-nowrap text-[13px]', ink)}>
          {due.timed && due.tone !== 'late' && <Glyph name="calendar" size={13} />}
          {due.words}
        </span>
        {due.sub && (
          <span className={cn('mt-px whitespace-nowrap text-[11.5px]', due.subTone === 'late' ? 'text-danger' : 'text-text-muted')}>
            {due.sub}
          </span>
        )}
      </span>
    )
  }

  const assignedCell = (row: ListRow) => {
    const faces = facesFor(row)
    if (faces.length === 0) {
      return (
        <span className="flex items-center gap-[7px] text-[13px] text-text-muted">
          <UnassignedFace size={22} />Unassigned
        </span>
      )
    }
    if (faces.length === 1) {
      return (
        <span className="flex min-w-0 items-center gap-[7px]">
          <Face person={faces[0]!} size={22} />
          <span className="truncate text-[13px]">{faces[0]!.name || str(row.assigneeName)}</span>
        </span>
      )
    }
    return (
      <span className="flex min-w-0 items-center gap-[7px]">
        <FaceStack people={faces} size={20} max={3} />
        <span className="truncate text-[13px]">{str(row.assignees)}</span>
      </span>
    )
  }

  const madeByCell = (row: ListRow) => {
    const name = str(row.createdByName)
    if (!name) return null
    const at = str(row.createdAt)
    const person = (directory?.people ?? []).find((p) => p.name === name)
    let when = ''
    if (at) {
      const local = zonedLocal(new Date(at), timeZone).date
      when = local === today ? 'Today' : local === addDays(today, -1) ? 'Yesterday'
        : local > addDays(today, -7) ? weekdayShort(local) : monthDay(local)
    }
    return (
      <span className="flex min-w-0 items-center gap-[7px]">
        {person && <Face person={person} size={22} />}
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-[13px]">{name}</span>
          {when && <span className="text-[11.5px] text-text-muted">{when}</span>}
        </span>
      </span>
    )
  }

  const groupLabel = (key: string): string => {
    if (list.view.group === 'dueGroup') return dueGroupLabel(key === '—' ? '' : key)
    if (list.view.group === 'bucket') return bucketKeyLabel(key === '—' ? '' : key)
    if (list.view.group === 'priority') return priorityKeyLabel(key)
    return key === '—' ? 'No value' : key
  }

  const bulk: BulkAction[] = [
    { key: 'mark_done', group: 'change', label: () => 'Mark done', icon: IconCheck },
    {
      key: 'change_due_date', group: 'change', label: () => 'Change due date', icon: IconCalendar,
      picker: (_ctx, done) => <DuePickerPanel today={today} onPick={(date) => done({ date })} />,
    },
    {
      key: 'reassign', group: 'change', label: () => 'Reassign', icon: IconPerson,
      picker: (_ctx, done) => (
        <MenuSelect
          label="Give them to"
          value={null}
          onChange={(userId) => { if (userId) done({ userId }) }}
          options={(directory?.people ?? []).filter((p) => p.book || p.id === me.user.id)
            .map((p) => ({ value: p.id, label: p.name, help: `${p.role} · ${p.place}` }))}
          placeholder="Pick a person"
          minWidth={240}
          className="w-full"
        />
      ),
    },
  ]

  return (
    <>
      <ListScreen
        area="tasks"
        title="Tasks"
        chrome="embedded"
        hostBar={hostBar}
        cards={false}
        list={shown}
        bulk={{ actions: bulk }}
        actions={(
          <Button variant="primary" size="toolbar"
            onClick={() => openActivityNew({ kind: 'task', prefill: listId ? { taskListId: listId } : {} })}>
            <IconPlus size={15} />New task
          </Button>
        )}
        primaryColumn={{
          field: 'title',
          header: 'Task',
          render: (row) => {
            const done = isDone(row)
            const priority = priorityOfKey(row.priority)
            const categories = categoriesOf(row)
            const checklist = checklistOf(row)
            const second = str(row.secondLine)
            /*
             * Planner's extras (§8.7): the priority before the title — its icon in its coloured chip, no word
             * (Aaron, Oct 26, answer 4) — the checklist and categories on the line under it.
             */
            return {
              /* A phone card wears it among its facts (`cardLine`) and only there — it was on both lines. */
              primary: done || priority === 'medium' || phone
                ? <span className={cn(done && 'text-text-faint line-through')}>{str(row.title)}</span>
                : (
                  <span className="inline-flex max-w-full items-center gap-1.5 align-middle">
                    <PriorityMark priority={priority} size="row" />
                    <span className="truncate">{str(row.title)}</span>
                  </span>
                ),
              secondary: phone || done ? undefined
                : checklist || categories.length > 0 ? (
                  <span className="inline-flex max-w-full items-center gap-1.5 align-middle">
                    <ChecklistBadge progress={checklist} size="row" />
                    <TaskCategoryChips categories={categories} max={2} />
                    {second && <span className="truncate">{second}</span>}
                  </span>
                )
                  : (second || undefined),
            }
          },
        }}
        mark={{
          width: 44,
          render: (row) => (
            <DoneCircle done={isDone(row)} late={row.late === true && !isDone(row)} size={phone ? 22 : 18}
              onToggle={() => void tick(row, !isDone(row))} />
          ),
        }}
        cardLine={(row) => {
          const due = rowDue(row, timeZone, today, isDone(row))
          const faces = facesFor(row)
          const link = linksOf(row)[0]
          const done = isDone(row)
          const priority = priorityOfKey(row.priority)
          const checklist = checklistOf(row)
          const category = categoriesOf(row)[0]
          return {
            facts: [
              ...(due ? [(
                <span key="due" className={cn('inline-flex items-center gap-[5px] text-[12.5px] font-semibold',
                  due.tone === 'late' ? 'text-danger' : due.tone === 'today' ? 'text-warn' : 'font-medium text-text-muted')}>
                  {due.timed && due.tone !== 'late' && <Glyph name="calendar" size={13} />}
                  {due.tone === 'late' && due.sub ? `${due.words} · ${due.sub}` : due.words}
                </span>
              )] : []),
              /* Planner's extras (§8.7), open tasks only. */
              ...(!done && priority !== 'medium' ? [<PriorityMark key="priority" priority={priority} size="row" />] : []),
              ...(!done && checklist ? [<ChecklistBadge key="checklist" progress={checklist} size="row" />] : []),
              ...(!done && category ? [<TaskCategoryChips key="category" categories={[category]} max={1} />] : []),
            ],
            /* Several people show their faces; inside a task list every card does (Board 19 E). */
            trailing: faces.length > 1 || (listId && faces.length > 0)
              ? <FaceStack people={faces} size={20} max={3} />
              : link ? <LinkChip type={link.type} label={link.label} /> : undefined,
          }
        }}
        cell={{
          dueAt: dueCell,
          attached: (row) => <LinkChips links={linksOf(row)} />,
          assignees: assignedCell,
          createdByName: madeByCell,
          /* Planner's extras as columns, when somebody adds them under Fields (§8.7). */
          /* A column about the priority says it, beside the chip the row's title wears. */
          priority: (row) => <PriorityMark priority={priorityOfKey(row.priority)} size="row" words />,
          categoryNames: (row) => (categoriesOf(row).length > 0
            ? <TaskCategoryChips categories={categoriesOf(row)} max={3} size="row" />
            : <span className="text-[13px] text-text-faint">—</span>),
        }}
        columnWidths={{ dueAt: 162, attached: 214, assignees: 168 }}
        groupLabel={(key) => groupLabel(key)}
        rowMenu={(row) => [
          { key: 'open', label: 'Open task', onSelect: () => openTaskSheet(row.id) },
          isDone(row)
            ? { key: 'reopen', label: 'Mark not done', onSelect: () => void tick(row, false) }
            : { key: 'done', label: 'Mark done', onSelect: () => void tick(row, true) },
        ]}
        onRowClick={(row) => openTaskSheet(row.id)}
        emptyTitle={listId ? 'Nothing in this list yet' : 'Nothing to do here'}
        emptyBody={listId ? 'Add a task to it, or move one in from the Board.' : 'Tasks for the people you have picked show here.'}
        importOrgUnitId={me.scope.selected[0]}
      />
      {toast && (
        <DoneToast
          title={`Done · ${toast.title}`}
          sub={toast.also}
          onUndo={() => {
            const row = list.rows.find((candidate) => candidate.id === toast.id)
            if (row) void tick(row, false)
            setToast(null)
          }}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  )
}

/** *Change due date*'s second step: a day, or no date at all. */
function DuePickerPanel({ today, onPick }: { today: string; onPick: (date: string | null) => void }) {
  const [day, setDay] = useState(today)
  return (
    <div className="flex flex-col gap-2 p-1">
      <DateField value={day} onChange={setDay} label="Due" size="field" display="day" />
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => onPick(null)} className="text-[12.5px] font-semibold text-text-muted hover:text-text">
          No date
        </button>
        <span className="flex-1" />
        <Button variant="primary" disabled={!day} onClick={() => onPick(day)}>Change</Button>
      </div>
    </div>
  )
}
